import {animate, style, transition, trigger} from '@angular/animations';
import {AfterViewInit, Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {SwPush} from '@angular/service-worker';
import {Affirmation, ApplicationFeature} from '../../models/applicationFeature';
import {AnnouncementsService} from '../../services/announcements.service';
import {CommonService} from '../../services/common.service';
import {ConsentsFormService} from '../../services/consents-form.service';
import {HeaderService} from '../../services/header.service';
import {LocalStorageService} from '../../services/local-storage.service';
import {NotificationService} from '../../services/notification.service';
import {ToastMessagesService} from '../../services/toast-messages.service';
import {ConfirmDialogComponent} from '../shared/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    trigger('panelInOut', [
      transition('void => *', [
        style({transform: 'translateY(-100%)'}),
        animate(800)
      ]),
      transition('* => void', [
        animate(300, style({transform: 'translateX(+100%)'}))
      ])
    ])
  ]
})
export class HomeComponent implements OnInit, AfterViewInit {
  affirmations: Affirmation[];
  allNotifications: any[];
  announcements: any[] = [];
  applicationFeatures: ApplicationFeature[] = [];
  bodyHeight: any;
  completedConsents: any[];
  consentNotifications: any[] = [];
  consents = [];
  currentUser: any;
  greetingMessage = '';
  historyScroll = false;
  loadingData: boolean;
  loadingCompletedConsents: boolean;
  loadingFeatures: boolean;
  loadingNotifications: boolean;
  notificationTitle: string;
  notifications: any[] = [];
  pendingAffirmations: any[] = [];
  pendingConsents: any[] = [];
  rowWidth: number;
  selectedMode = null;
  displayMode = 'consents';
  isMobileLoading: boolean;
  // For backward compatibility with existing components
  children: any[] = [];
  selectedChildren: any[] = [];
  selectedChild: any = null;
  selectedMobileChild: any = null;
  loadingChildren: boolean;

  constructor(private localStorageService: LocalStorageService,
              public toastMessagesService: ToastMessagesService,
              private headerService: HeaderService,
              private commonService: CommonService,
              public dialog: MatDialog,
              private announcementsService: AnnouncementsService,
              public router: Router,
              public swPush: SwPush,
              private notificationService: NotificationService,
              public consentsFormService: ConsentsFormService) {

    this.currentUser = JSON.parse(this.localStorageService.get('user'));
    this.bodyHeight = window.innerHeight - 200;
    this.notifications = [];
    this.completedConsents = [];
    this.children = [];
    this.rowWidth = window.innerWidth;
    this.isMobileLoading = true;

    const curHr = new Date().getHours();

    if (curHr < 12) {
      this.greetingMessage = 'Καλημέρα';
    } else {
      this.greetingMessage = 'Καλησπέρα';
    }
  }

  async ngOnInit() {
    this.loadingData = true;
    this.loadingChildren = true;
    this.loadingFeatures = false;
    this.loadingNotifications = true;
    this.loadingCompletedConsents = true;
    this.headerService.setHeaderTitle(`Οι δηλώσεις μου`);
  }

  async ngAfterViewInit() {
    await this.initAffirmations();
    await this.initNotifications(false);
    await this.initAnnouncements(false);
    await this.initializeUserData();
    await this.initializeCompletedConsents();

    this.swPush.messages.subscribe(async (message) => {
      this.toastMessagesService.toastMessages(`Έχετε ειδοποιήσεις`);
      await this.initNotifications(true);
      await this.initAnnouncements(true);
    });
  }

//#region Requests
  async initAnnouncements(force: boolean) {
    try {
      await this.announcementsService.getAllAnnouncements(+this.currentUser.id, force);

      this.announcements = this.announcementsService.announcements;

      for (const announcement of this.announcements) {
        if (announcement.is_opened === 0) {
          announcement.doShow = true;
        }
      }
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingNotifications = false;
    }
  }

  async initNotifications(force: boolean) {
    try {
      // const notificationRequest = await this.commonService.getList(`people/parents/notifications?parentId=${this.currentUser.id}`);
      await this.notificationService.getAllNotifications(+this.currentUser.id, force);

      this.notifications = this.notificationService.getInCompleteNotifications();
      this.consentNotifications = [];
      this.consentNotifications = this.notifications;
      // this.consentNotifications.sort((a, b) => a.notificationDate - b.notificationDate);
      // this.consentNotifications = this.notifications.filter(notification => notification.mode !== 'display');
      this.notificationTitle = this.notifications.length === 0 ? 'Δεν έχετε εκκρεμείς δηλώσεις' : `Δηλώσεις προς συμπλήρωση`;
    } catch (e) {
      console.log(e);
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingNotifications = false;
    }
  }

  /**
   * Initialize user data for Single User Model (no children)
   * Creates a virtual "user" entry that works with existing components
   */
  async initializeUserData() {
    try {
      // Create a single user entry (the user themselves) for backward compatibility
      const userEntry = {
        id: this.currentUser.id,
        firstName: this.currentUser.firstName || this.currentUser.first_name,
        lastName: this.currentUser.lastName || this.currentUser.last_name,
        notifications: [],
        affirmations: [],
        announcements: [],
        currentYearNotifications: [],
        pastYearNotifications: []
      };

      // Process notifications for the user
      this.pendingConsents = this.notifications.filter(notification => notification.form_type_id === 1);
      this.pendingAffirmations = this.notifications.filter(notification => notification.form_type_id === 2);

      for (const consent of this.notifications) {
        let date = '';
        if (consent.notificationDate && consent.notificationDate.includes('-')) {
          const tempDate = consent.notificationDate.split('-');
          const day = tempDate[0];
          const month = tempDate[1];
          const tempYear = tempDate[2].split(' ');
          const year = tempYear[0];
          date = `${year}-${month}-${day}`;
        }

        const notificationModel = {
          id: consent.id,
          formId: consent.formId,
          form_type_id: consent.form_type_id,
          unique_id: consent.unique_id,
          description: consent.description,
          title: consent.formTitle,
          subTitle: consent.deadline ? consent.deadline : '-',
          deadline: consent.deadline ? consent.deadline : '-',
          notificationDate: consent.notificationDate,
          formattedNotificationDate: date,
          mode: consent.mode,
          childId: this.currentUser.id // For backward compatibility
        };
        userEntry.notifications.push(notificationModel);
      }

      // Add announcements
      for (const announcement of this.announcements) {
        userEntry.announcements.push(announcement);
      }

      userEntry.currentYearNotifications = this.currentYearNotifications(userEntry.notifications);
      userEntry.pastYearNotifications = this.pastYearNotifications(userEntry.notifications);

      // Set children array with just the user for backward compatibility
      this.children = [userEntry];
      this.selectedChildren = [userEntry];

    } catch (e) {
      console.log(e.message);
    } finally {
      this.loadingChildren = false;
      this.loadingData = false;
    }
  }

  async initializeFeatures() {
    try {
      const featureRequest = await this.commonService.getList('features');
      this.applicationFeatures = featureRequest.object;
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
      console.log(e.message);
    } finally {
      this.loadingFeatures = false;
    }
  }

  async initializeCompletedConsents() {
    try {
      this.completedConsents = [];
      this.completedConsents = this.notificationService.getCompletedNotifications();
    } catch (e) {
      this.toastMessagesService.toastErrorMessage(e.message);
    } finally {
      this.loadingCompletedConsents = false;
    }
  }

  async initAffirmations() {
    // try {
    //   const affirmationRequest = await this.commonService.getList(`affirmations/active`);
    //   this.affirmations = affirmationRequest.object as Affirmation[];
    // } catch (e) {
    //   this.toastMessagesService.toastErrorMessage(e.message);
    // }
  }

  closeFloatingNotification(notification, valueEmitted) {
    notification.doShow = valueEmitted;
  }

  //#endregion


  childSelected($event: any) {
    const index = this.selectedChildren.findIndex(child => child.id === $event.id);
    if (index === -1) {
      this.selectedChildren.push($event);
    }
  }

  deselectChild($event: number) {
    const index = this.selectedChildren.findIndex(child => child.id === $event);
    if (index !== -1) {
      this.selectedChildren.splice(index, 1);
    }
  }

  async showConsent(notification: any) {
    // Single User Model - user acts for themselves
    await this.consentsFormService.showUserForm(notification, this.currentUser);
  }

  childrenDisplayName(child: any) {
    return `${child.firstName} ${child.lastName}`;
  }

  selectChild(child, $element) {
    this.selectedChild = child;
    $element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
    setTimeout(() => {
      $element.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
    }, 500);

  }

  scrollToHistory($event: any) {
    this.historyScroll = !this.historyScroll;
  }

  promptLogout() {
    const confirm = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Έξοδος',
        message: 'Είστε σίγουροι οτι θέλετε να αποσυνδεθείτε?',
      }
    });

    confirm.afterClosed().subscribe(async (response) => {
      if (response) {
        await this.logout();
      }
    });
  }

  async logout() {
    this.localStorageService.clear('user');
    this.localStorageService.clear('token');
    await this.router.navigate(['login']);
  }

  childConsentsNumber(child: any) {
    const consents = child.notifications.filter(notification => notification.mode === 'consent');
    return consents.length;
  }

  childAffirmationsNumber(child: any) {
    const affirmations = child.notifications.filter(notification => notification.mode === 'affirmation');
    return affirmations.length;
  }

  childCustomFormNumber(child: any) {
    const customForms = child.notifications.filter(notification => notification.mode === 'customForm');
    return customForms.length;
  }

  isNotificationNumberVisible(child) {
    const consents = child.notifications.filter(notification => notification.mode === 'consent');
    return consents.length === 0;
  }

  isAffirmationNumberVisible(child) {
    const affirmations = child.notifications.filter(notification => notification.mode === 'affirmation');
    return affirmations.length === 0;
  }

  isCustomFormsNumberVisible(child: any) {
    const customForms = child.notifications.filter(notification => notification.mode === 'customForm');
    return customForms.length === 0;
  }

  childAnnouncementsNumber(child: any) {
    const announcements = this.announcementsService.announcements.filter(announcement => announcement.child_id === child.id);
    return announcements.length;
  }

  isAnnouncementNumberVisible(child: any) {
    const announcements = this.announcementsService.announcements.filter(announcement => announcement.child_id === child.id);
    return announcements.length === 0;
  }

  returnFromForms() {
    this.selectedMobileChild = null;
    this.selectedMode = null;
  }

  gotToForms(child, mode) {
    this.selectedMode = mode;
    this.selectedMobileChild = child;
  }

  scroll(id) {
    const el = document.getElementById(id);
    // el.scrollIntoView(true);
    el.scrollIntoView({behavior: 'smooth'});
  }

  async showChildConsent(form: any) {
    console.log(form);
    const inputConfig = {
      parentId: this.currentUser.id,
      childId: form.childId,
      mode: false,
      unique_id: form.unique_id,
      notificationId: form.id
    };
    // await this.router.navigate(['/home/requestFrom'], {queryParams: inputConfig});
  }

  setSelectedMode($event: any) {
    this.displayMode = $event;
  }

  updateMessage(event) {

  }

  currentYearNotifications(notifications: any) {
    const currentSchoolYear = this.getCurrentYear();
    return notifications.filter(notification => new Date(notification.formattedNotificationDate) >= currentSchoolYear);
  }

  pastYearNotifications(notifications: any) {
    const currentSchoolYear = this.getCurrentYear();
    return notifications.filter(notification => new Date(notification.formattedNotificationDate) < currentSchoolYear);
  }

  getCurrentYear() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    let currentSchoolYear;
    if (currentMonth < 9 && currentMonth >= 1) {
      const pastYear = new Date();
      pastYear.setFullYear(pastYear.getFullYear() - 1);
      currentSchoolYear = new Date(pastYear.getFullYear(), 8, 1);
    } else {
      currentSchoolYear = new Date(currentYear, 8, 1);
    }

    return currentSchoolYear;
  }
}
